import { useState } from 'react';
import { LoginForm } from '@/components/auth/LoginForm';
import { SignupForm } from '@/components/auth/SignupForm';
import { ConfirmSignup } from '@/components/auth/ConfirmSignup';
import { OrbBackground } from '@/components/auth/OrbBackground';
import ForGenAI from '@/components/auth/ForGenAI';

interface LandingProps {
  onLoginSuccess: (token: string, username: string) => void;
}

type ViewType = 'login' | 'signup' | 'confirm';

export default function Landing({ onLoginSuccess }: LandingProps) {
  const [currentView, setCurrentView] = useState<ViewType>('login');
  const [signupUsername, setSignupUsername] = useState('');

  const handleSignupSuccess = (username: string) => {
    setSignupUsername(username);
    setCurrentView('confirm');
  };

  const handleSwitchToConfirm = () => {
    setSignupUsername(''); // Clear username so user can enter it manually
    setCurrentView('confirm');
  };

  const handleConfirmSuccess = () => {
    alert('Account confirmed successfully! Please log in.');
    setCurrentView('login');
  };

  return (
    <div className="min-h-screen w-full relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-center p-4 overflow-hidden">
      {/* Animated Orb Background */}
      <OrbBackground />

      {/* Logo */}
      <div className="mb-8 flex flex-col items-center relative z-10 animate-fade-in">
        <div className="mb-4 transform hover:scale-105 transition-transform duration-300">
          <div className="relative inline-block">
            <h1 className="text-6xl text-white text-center bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent animate-gradient-x" style={{ fontFamily: 'Baumans, cursive' }}>
              Patent Loop
            </h1>
            <span className="absolute -top-2 -right-16 px-3 py-1 text-xs font-bold bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg shadow-lg animate-pulse">
              BETA
            </span>
          </div>
          <p className="text-white text-center mt-4 text-xl font-semibold tracking-wider drop-shadow-lg" style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '0.05em' }}>
            AI-Powered Patent Analysis
          </p>
        </div>
        {/* Decorative elements */}
        <div className="absolute -top-20 -left-20 w-40 h-40 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -top-10 -right-20 w-32 h-32 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-700"></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10">
        {currentView === 'login' && (
          <LoginForm
            onLoginSuccess={onLoginSuccess}
            onSwitchToSignup={() => setCurrentView('signup')}
            onSwitchToConfirm={handleSwitchToConfirm}
          />
        )}

        {currentView === 'signup' && (
          <SignupForm
            onSwitchToLogin={() => setCurrentView('login')}
            onSignupSuccess={handleSignupSuccess}
            onSwitchToConfirm={handleSwitchToConfirm}
          />
        )}

        {currentView === 'confirm' && (
          <ConfirmSignup
            username={signupUsername}
            onConfirmSuccess={handleConfirmSuccess}
            onBackToLogin={() => setCurrentView('login')}
          />
        )}
      </div>

      {/* Footer */}
      <div className="mt-8 text-center relative z-10">
        <p className="text-white/60 text-sm">
          © 2025 Patent Loop. All rights reserved.
        </p>
        <p className="text-white/40 text-xs mt-2">
          For support, contact: support@patentloop.com
        </p>
      </div>

      {/* ForGen AI Logo */}
      <ForGenAI />
    </div>
  );
}
